import type { ReactElement } from 'react';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';

export interface FieldErrorProps {
  /** Already-translated error message. Component is i18n-agnostic. */
  error?: string | undefined;
  /** Optional id for aria-describedby wiring from the associated input. */
  id?: string | undefined;
  /** Optional test id override (default: 'field-error'). */
  testId?: string | undefined;
  /** Extra class names to merge with the base. */
  className?: string | undefined;
}

/**
 * Animated inline error label for form fields.
 *
 * Contract (spec: field-error-ui):
 * - Returns null when `error` is falsy (undefined, null, empty string).
 * - Otherwise renders <div role="alert" aria-live="polite"> with the error text.
 * - Color comes from useTheme().C.danger — no hardcoded palette outside the design system.
 * - Self-contained <style> block declares two keyframes: shake + fade-in.
 * - The shake animation is applied only when `prefers-reduced-motion: no-preference`
 *   matches — clients requesting reduced motion never see it. Fade-in always applies.
 */
export function FieldError({ error, id, testId, className }: FieldErrorProps): ReactElement | null {
  const { C } = useTheme();

  if (!error) return null;

  const baseClass = 'mi-field-error';
  const finalClass = className ? `${baseClass} ${className}` : baseClass;

  return (
    <>
      <style>{`
        @keyframes miFieldErrorFadeIn {
          from { opacity: 0; transform: translateY(-2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes miFieldErrorShake {
          0%, 100% { transform: translateX(0); }
          20%      { transform: translateX(-3px); }
          40%      { transform: translateX(3px); }
          60%      { transform: translateX(-2px); }
          80%      { transform: translateX(2px); }
        }
        .mi-field-error {
          animation: miFieldErrorFadeIn 180ms ease-out both;
        }
        @media (prefers-reduced-motion: no-preference) {
          .mi-field-error {
            animation: miFieldErrorFadeIn 180ms ease-out both,
                       miFieldErrorShake 320ms ease-in-out 180ms both;
          }
        }
      `}</style>
      <div
        role="alert"
        aria-live="polite"
        data-testid={testId ?? 'field-error'}
        id={id}
        className={finalClass}
        style={{
          fontSize: 10,
          fontWeight: 600,
          color: C.danger,
          marginTop: 4,
          lineHeight: 1.3,
        }}
      >
        {error}
      </div>
    </>
  );
}
